import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import TopBar from './TopBar';
import { getOrdersByCustomerId, getOrderItems, getOrderItemsByFoodIds, getOrderById } from '../services/orderService';
import { getPickupPointById } from '../services/pickupPointService';
import { getFoodItemById, getFoodItemsByVendorId, getFoodImageUrl } from '../services/foodItemService';
import { getProfileByUserId, updateProfile } from '../services/profileService';
import './OrdersPage.css';

const OrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [viewMode, setViewMode] = useState('buyer'); // 'buyer' or 'vendor'
  const [tab, setTab] = useState('all');
  const [orders, setOrders] = useState([]);
  const [vendorOrders, setVendorOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isVendor, setIsVendor] = useState(false);
  const [notificationSaving, setNotificationSaving] = useState(false);

  // Toggle sales notification preference for vendor
  const handleToggleSalesNotification = async (e) => {
    // Prevent click bubbling if used inside clickable containers
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (!userProfile) return;
    const newVal = !userProfile.sales_notification;
    try {
      setNotificationSaving(true);
      await updateProfile(userProfile.$id, { sales_notification: newVal });
      setUserProfile(prev => ({ ...(prev || {}), sales_notification: newVal }));
      showSuccess(`Notifications ${newVal ? 'enabled' : 'disabled'} successfully!`);
    } catch (err) {
      console.error('Error updating sales notification preference:', err);
      showError('Failed to update notification preference. Please try again.');
    } finally {
      setNotificationSaving(false);
    }
  };

  // Check if user is a vendor
  useEffect(() => {
    const checkVendorStatus = async () => {
      if (!user) return;
      try {
        const profile = await getProfileByUserId(user.$id);
        setUserProfile(profile);
        setIsVendor(profile?.role === 'vendor');
      } catch (err) {
        console.error('Error checking vendor status:', err);
      }
    };
    checkVendorStatus();
  }, [user]);

  // Restore view mode and tab from navigation state if provided (used when returning from a detail view)
  const location = useLocation();
  useEffect(() => {
    const state = location && location.state ? location.state : {};
    if (state.viewMode === 'vendor' && isVendor) {
      setViewMode('vendor');
    } else if (state.viewMode === 'buyer') {
      setViewMode('buyer');
    }
    if (state.tab) setTab(state.tab);
  }, [location && location.state, isVendor]);

  // Fetch buyer orders
  useEffect(() => {
    const fetchBuyerOrders = async () => {
      if (!user || viewMode !== 'buyer') return;

      try {
        setLoading(true);
        setError(null);

        const dbOrders = await getOrdersByCustomerId(user.$id);

        const enrichedOrders = await Promise.all(
          dbOrders.map(async (order) => {
            // We'll determine pickup locations per item (derive from each food's pickup_id)
            const pickupCache = {};

            // Get order items
            let items = [];
            let overallStatus = 'pending';
            try {
              const orderItems = await getOrderItems(order.$id);
              items = await Promise.all(
                orderItems.map(async (item) => {
                  let foodData = null;
                  try {
                    foodData = await getFoodItemById(item.food_id);
                  } catch (foodError) {
                    console.error('Error fetching food item:', foodError);
                  }

                  // Resolve pickup location from the food item
                  let pickupId = foodData?.pickup_id || null;
                  let pickupLocationName = 'Not specified';
                  if (pickupId) {
                    if (!pickupCache[pickupId]) {
                      try {
                        const pickupPoint = await getPickupPointById(pickupId);
                        pickupCache[pickupId] = pickupPoint?.fullName || pickupPoint?.name || 'Not specified';
                      } catch (pickupErr) {
                        console.error('Error fetching pickup point for item:', pickupErr);
                        pickupCache[pickupId] = 'Not specified';
                      }
                    }
                    pickupLocationName = pickupCache[pickupId];
                  }

                  return {
                    id: item.$id,
                    name: foodData?.item_name || 'Unknown Item',
                    quantity: item.quantity,
                    price: item.price_at_purchase,
                    status: item.status || 'pending',
                    imageUrl: foodData?.image_id ? getFoodImageUrl(foodData.image_id) : null,
                    pickupTime: foodData?.pickup_time || 'Not specified',
                    pickupId: pickupId,
                    pickupLocation: pickupLocationName
                  };
                })
              );

              // Calculate overall status based on item statuses
              const statuses = items.map(i => i.status);
              const allCompleted = statuses.length > 0 && statuses.every(s => s === 'completed');
              const allNotCompleted = statuses.length > 0 && statuses.every(s => s === 'not_completed');
              const allTerminal = statuses.length > 0 && statuses.every(s => s === 'completed' || s === 'not_completed');

              if (allCompleted) {
                overallStatus = 'completed';
              } else if (allNotCompleted) {
                // All items reported not received
                overallStatus = 'not_completed';
              } else if (allTerminal && statuses.some(s => s === 'not_completed')) {
                // Mixed completed and not_completed -> finished but partially not received
                overallStatus = 'partial';
              } else if (statuses.every(s => s === 'ready_for_pickup' || s === 'completed')) {
                overallStatus = 'ready_for_pickup';
              } else if (statuses.some(s => s === 'preparing')) {
                overallStatus = 'preparing';
              }
            } catch (itemsError) {
              console.error('Error fetching order items:', itemsError);
            }

            const orderDate = new Date(order.order_date);
            const formattedDate = orderDate.toLocaleDateString('en-US', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            }) + ', ' + orderDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });

            const uniquePickupTimes = Array.from(new Set(items.map(i => i.pickupTime).filter(Boolean)));
            const uniquePickupLocations = Array.from(new Set(items.map(i => i.pickupLocation || 'Not specified')));
            const pickupLocationSummary = uniquePickupLocations.length === 1 ? uniquePickupLocations[0] : 'Multiple pickup locations';

            return {
              id: order.$id,
              status: overallStatus,
              date: formattedDate,
              // Raw order date preserved for reliable sorting if needed
              orderDateRaw: order.order_date,
              pickup: pickupLocationSummary,
              pickupTimes: uniquePickupTimes,
              totalAmount: order.total_amount,
              pointsRedeemed: order.points_redeemed,
              items: items
            };
          })
        );

        setOrders(enrichedOrders);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load orders. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchBuyerOrders();
  }, [user, viewMode]);

  // Fetch vendor orders (order items for their food items)
  useEffect(() => {
    const fetchVendorOrders = async () => {
      if (!user || viewMode !== 'vendor' || !isVendor) return;

      try {
        setLoading(true);
        setError(null);

        // Get all food items by this vendor
        const vendorFoodItems = await getFoodItemsByVendorId(user.$id);
        const foodIds = vendorFoodItems.map(item => item.$id);

        if (foodIds.length === 0) {
          setVendorOrders([]);
          setLoading(false);
          return;
        }

        // Get all order items for these food items
        const orderItems = await getOrderItemsByFoodIds(foodIds);

        // Enrich order items with food details, customer info, and pickup info
        const enrichedOrderItems = await Promise.all(
          orderItems.map(async (orderItem) => {
            // Get food item details
            const foodItem = vendorFoodItems.find(f => f.$id === orderItem.food_id);

            // Get order details for customer info
            let customerName = 'Unknown Customer';
            let pickupLocation = 'Not specified';
            let pickupTime = foodItem?.pickup_time || 'Not specified';
            let orderDate = orderItem.$createdAt;

            try {
              const order = await getOrderById(orderItem.order_id);
              orderDate = order.order_date;

              // Get customer profile
              try {
                const customerProfile = await getProfileByUserId(order.customer_id);
                customerName = customerProfile?.full_name || 'Unknown Customer';
              } catch (customerError) {
                console.error('Error fetching customer:', customerError);
              }

              // Get pickup location from the food item (not order)
              if (foodItem?.pickup_id) {
                try {
                  const pickupPoint = await getPickupPointById(foodItem.pickup_id);
                  pickupLocation = pickupPoint.fullName || pickupPoint.name;
                } catch (pickupError) {
                  console.error('Error fetching pickup:', pickupError);
                }
              }
            } catch (orderError) {
              console.error('Error fetching order:', orderError);
            }

            const date = new Date(orderDate);
            const formattedDate = date.toLocaleDateString('en-US', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            }) + ', ' + date.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });

            return {
              id: orderItem.$id,
              orderId: orderItem.order_id,
              foodId: orderItem.food_id,
              foodName: foodItem?.item_name || 'Unknown Item',
              quantity: orderItem.quantity,
              priceAtPurchase: orderItem.price_at_purchase,
              status: orderItem.status || 'pending',
              customerName: customerName,
              pickupLocation: pickupLocation,
              pickupTime: pickupTime,
              // Raw order date preserved for sorting/tie-breakers
              orderDateRaw: orderDate,
              date: formattedDate,
              imageUrl: foodItem?.image_id ? getFoodImageUrl(foodItem.image_id) : null
            };
          })
        );

        setVendorOrders(enrichedOrderItems);
      } catch (err) {
        console.error('Error fetching vendor orders:', err);
        setError('Failed to load orders. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchVendorOrders();
  }, [user, viewMode, isVendor]);

  // Filter orders based on tab
  const filteredBuyerOrders = orders.filter(order => {
    if (tab === 'all') return true;
    if (tab === 'active') {
      // Active = has any non-terminal status (still pending/preparing/ready_for_pickup)
      return !(order.status === 'completed' || order.status === 'not_completed' || order.status === 'partial');
    }
    if (tab === 'past') {
      // Past = finished orders (all items either completed or not_completed, including partial)
      return order.status === 'completed' || order.status === 'not_completed' || order.status === 'partial';
    }
    return true;
  });

  const filteredVendorOrders = vendorOrders.filter(order => {
    if (tab === 'all') return true;
    if (tab === 'active') {
      // Active = has any non-terminal status (still pending/preparing/ready_for_pickup)
      return !(order.status === 'completed' || order.status === 'not_completed' || order.status === 'partial');
    }
    if (tab === 'past') {
      // Past = finished orders (all items either completed or not_completed, including partial)
      return order.status === 'completed' || order.status === 'not_completed' || order.status === 'partial';
    }
    return true;
  });

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'preparing': return 'Preparing';
      case 'ready_for_pickup': return 'Ready for Pickup';
      case 'completed': return 'Completed';
      case 'not_completed': return 'Not Completed';
      case 'partial': return 'Partially Complete';
      default: return 'Unknown';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'preparing': return 'status-preparing';
      case 'ready_for_pickup': return 'status-ready';
      case 'completed': return 'status-completed';
      case 'not_completed': return 'status-not-completed';
      case 'partial': return 'status-partial';
      default: return '';
    }
  };

  const formatOrderId = (id) => {
    if (!id) return '';
    // Display the raw Appwrite document ID ($id) from the order document
    return id;
  };

  const formatPickupTime = (timeString) => {
    if (!timeString || timeString === 'Not specified') return 'Not specified';
    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return timeString;
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }) + ', ' + date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  // --- Sorting helpers (sort by pickup datetime ascending) ---
  const parseTimeMs = (timeString) => {
    if (!timeString) return Infinity;
    const ms = Date.parse(timeString);
    return Number.isFinite(ms) ? ms : Infinity;
  };

  const getEarliestPickupMs = (pickupTimes) => {
    if (!Array.isArray(pickupTimes) || pickupTimes.length === 0) return Infinity;
    const msValues = pickupTimes.map(pt => parseTimeMs(pt)).filter(v => Number.isFinite(v));
    if (msValues.length === 0) return Infinity;
    return Math.min(...msValues);
  };

  // Status priority mapping so we can group orders by status before sorting by time.
  // Lower number = higher priority in the list.
  const _statusPriority = {
    'ready_for_pickup': 0,
    'preparing': 1,
    'pending': 2,
    'completed': 3,
    // Any other statuses (e.g., not_completed, partial) will follow
    'not_completed': 4,
    'partial': 5
  };

  const getStatusPriority = (status) => {
    return _statusPriority[status] !== undefined ? _statusPriority[status] : 99;
  };

  // Sort filtered lists by status priority first (Ready -> Preparing -> Pending -> Completed),
  // then by earliest pickup time (ties resolved by order date).
  const sortedFilteredBuyerOrders = filteredBuyerOrders.slice().sort((a, b) => {
    const pa = getStatusPriority(a.status);
    const pb = getStatusPriority(b.status);

    if (pa !== pb) return pa - pb;

    const aMs = getEarliestPickupMs(a.pickupTimes);
    const bMs = getEarliestPickupMs(b.pickupTimes);

    if (aMs === bMs) {
      // Fall back to order date (raw) if available
      const aDate = a.orderDateRaw ? Date.parse(a.orderDateRaw) : Infinity;
      const bDate = b.orderDateRaw ? Date.parse(b.orderDateRaw) : Infinity;
      return aDate - bDate;
    }
    return aMs - bMs;
  });

  const sortedFilteredVendorOrders = filteredVendorOrders.slice().sort((a, b) => {
    const pa = getStatusPriority(a.status);
    const pb = getStatusPriority(b.status);

    if (pa !== pb) return pa - pb;

    const aMs = parseTimeMs(a.pickupTime);
    const bMs = parseTimeMs(b.pickupTime);

    if (aMs === bMs) {
      const aDate = a.orderDateRaw ? Date.parse(a.orderDateRaw) : Infinity;
      const bDate = b.orderDateRaw ? Date.parse(b.orderDateRaw) : Infinity;
      return aDate - bDate;
    }
    return aMs - bMs;
  });

  return (
    <div className="orders-page">
      <TopBar />
      <div className="orders-container">
        <h2 className="orders-title">My Orders</h2>

        {/* View Mode Tabs (only show if user is a vendor) */}
        {isVendor && (
          <div>
            <div className="view-mode-tabs">
              <button
                className={viewMode === 'buyer' ? 'active' : ''}
                onClick={() => { setViewMode('buyer'); setTab('all'); }}
              >
                My Purchases
              </button>
              <button
                className={viewMode === 'vendor' ? 'active' : ''}
                onClick={() => { setViewMode('vendor'); setTab('all'); }}
              >
                Customer Orders
              </button>
            </div>

          </div>
        )}

        {/* Status Filter Tabs */}
        <div className="orders-tabs">
          <button className={tab === 'all' ? 'active' : ''} onClick={() => setTab('all')}>All</button>
          <button className={tab === 'active' ? 'active' : ''} onClick={() => setTab('active')}>Active</button>
          <button className={tab === 'past' ? 'active' : ''} onClick={() => setTab('past')}>Past</button>
        </div>

        {loading ? (
          <div className="orders-loading">
            <div className="orders-spinner"></div>
            <p>Loading your orders...</p>
          </div>
        ) : error ? (
          <div className="orders-error">
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="retry-btn">
              Retry
            </button>
          </div>
        ) : viewMode === 'buyer' ? (
          /* BUYER VIEW */
          <div className="orders-list">
            <div className="buyer-sort-hint">Sorted by earliest pickup time among order items (ties resolved by order date)</div>
            {filteredBuyerOrders.length === 0 ? (
              <div className="no-orders">
                <div className="no-orders-icon">📦</div>
                <p>No orders found.</p>
                <button onClick={() => navigate('/buy-food')} className="browse-btn">
                  Browse Food
                </button>
              </div>
            ) : (
              sortedFilteredBuyerOrders.map(order => (
                <div
                  className="order-card clickable"
                  key={order.id}
                  onClick={() => navigate(`/orders/buyer/${order.id}`, { state: { viewMode: 'buyer', tab } })}
                >
                  <div className={`order-status ${getStatusClass(order.status)}`}>
                    {getStatusText(order.status)}
                  </div>
                  <div className="order-id">
                    Order ID: <span className="order-id-value">{formatOrderId(order.id)}</span>
                  </div>
                  <div className="order-date">{order.date}</div>

                  {/* Order Items Preview */}
                  {order.items && order.items.length > 0 && (
                    <div className="order-items">
                      {order.items.slice(0, 3).map((item, index) => (
                        <div key={index} className="order-item-row">
                          <span className="item-quantity">{item.quantity}x</span>
                          <span className="item-name">{item.name}</span>
                          <span className={`item-status ${getStatusClass(item.status)}`}>
                            {getStatusText(item.status)}
                          </span>
                        </div>
                      ))}
                      {order.items.length > 3 && (
                        <div className="more-items">+{order.items.length - 3} more items</div>
                      )}
                    </div>
                  )}

                  <div className="order-total">
                    <span>Total:</span>
                    <span className="total-amount">RM {order.totalAmount?.toFixed(2) || '0.00'}</span>
                  </div>

                  {order.pointsRedeemed > 0 && (
                    <div className="order-points-redeemed">
                      Points redeemed: {order.pointsRedeemed}
                    </div>
                  )}

                  <div className="order-pickup">
                    <span className="pickup-label">Pickup Location:</span> {order.pickup}
                  </div>
                  {order.pickupTimes && order.pickupTimes.length > 0 && (
                    <div className="order-pickup-time">
                      <span className="pickup-label">{order.pickupTimes.length > 1 ? 'Pickup Times:' : 'Pickup Time:'}</span>
                      <span className="pickup-time-value">{order.pickupTimes.map(pt => formatPickupTime(pt)).join(', ')}</span>
                    </div>
                  )}
                  <div className="view-details-hint">
                    Click to view details →
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* VENDOR VIEW */
          <div className="orders-list vendor-orders">
            {/* Vendor name shown to remind which vendor account is active */}
            {userProfile?.full_name && (
              <div className="vendor-name">
                <span className="vendor-name-label">Your Vendor Name:</span>
                <span className="vendor-name-value">{userProfile.vendor_name}</span>
              </div>
            )}

            {/* Notification toggle for vendors */}
            <div className="vendor-settings">
              <label className="toggle-label">
                Sales Notifications
                <div className="switch" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={!!userProfile?.sales_notification}
                    onChange={handleToggleSalesNotification}
                    disabled={notificationSaving}
                    aria-label="Toggle sales notifications"
                  />
                  <span className="slider"></span>
                </div>
              </label>
              <div className="toggle-note">Receive email notifications when new orders are placed</div>
            </div>
            {/* Hint for vendors explaining the sort order */}
            <div className="vendor-sort-hint"> Sorted by pickup time (earliest first)</div>
            {filteredVendorOrders.length === 0 ? (
              <div className="no-orders">
                <div className="no-orders-icon">📋</div>
                <p>No customer orders found.</p>
                <button onClick={() => navigate('/sell-food')} className="browse-btn">
                  Manage Your Listings
                </button>
              </div>
            ) : (
              sortedFilteredVendorOrders.map(orderItem => (
                <div
                  className="order-card vendor-order-card clickable"
                  key={orderItem.id}
                  onClick={() => navigate(`/orders/vendor/${orderItem.id}`, { state: { viewMode: 'vendor', tab } })}
                >
                  <div className={`order-status ${getStatusClass(orderItem.status)}`}>
                    {getStatusText(orderItem.status)}
                  </div>

                  <div className="vendor-order-content">
                    <div className="vendor-order-image">
                      {orderItem.imageUrl ? (
                        <img src={orderItem.imageUrl} alt={orderItem.foodName} />
                      ) : (
                        <div className="image-placeholder">🍽️</div>
                      )}
                    </div>
                    <div className="vendor-order-details">
                      <h3 className="food-name">{orderItem.foodName}</h3>
                      <p className="order-quantity">Quantity: {orderItem.quantity}</p>
                      <p className="order-id-small">Order Item ID: {formatOrderId(orderItem.id)}</p>
                      <p className="customer-name">Customer: {orderItem.customerName}</p>
                      <p className="pickup-info">Pickup Location: {orderItem.pickupLocation}</p>
                      <p className="pickup-time">Pickup Time: {formatPickupTime(orderItem.pickupTime)}</p>
                    </div>
                    <div className="vendor-order-price">
                      <span className="price">RM {(orderItem.priceAtPurchase * orderItem.quantity).toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="order-date">{orderItem.date}</div>

                  <div className="view-details-hint">
                    Click to manage order →
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default OrdersPage;
